import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import api from '../../utils/api';
import { useAuth } from '../../auth/useAuth';
import { useRBAC } from '../../auth/useRBAC';
import { acceptMePermissions } from '../../auth/mePermissions';
import { ROLE_LABELS } from '../../auth/roles';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import { OfflineNotice, useIsOffline } from '../../offline';
import {
  PERMISSION_CATALOG_KEY,
  getPermissionCatalog,
  getUserPermissions,
  putUserPermissions,
  userPermissionsKey,
} from '../../services/userPermissionsService';
import type {
  PermissionCatalog,
  PermissionOverrideInput,
  PermissionSlug,
  UserPermissionsView,
} from '../../services/userPermissionsService';
import { noteFor } from './permissionNotes';
import {
  CEILING_BLOCK_REASON,
  applyReset,
  buildEditorState,
  ceilingBlocks,
  customizedCount,
  firstNameOf,
  isCustomized,
  isDirty,
  narrowedSlugs,
  redundantOverrideSlugs,
  resetBlockedReason,
  syncNarrowedSlugs,
  toOverrides,
  toggle,
  visibleCategories,
  visibleCustomizedCount,
} from './permissionEditorModel';
import type { EditorState } from './permissionEditorModel';
import { IosVersionNote } from './IosNarrowingGate';
import type { TeamUser } from './teamTypes';

/**
 * Revoking either of these from YOURSELF takes away the route back to this
 * screen: the Team tab is gated on `manage:users` and the editor on
 * `manage:roles_permissions`. The clinic's ≥1-holder floor does not cover it
 * (another holder may exist), so the server would accept the save — which is
 * exactly why the confirm is here and not there.
 */
const SELF_LOCKOUT_SLUGS: PermissionSlug[] = ['manage:roles_permissions', 'manage:users'];

/**
 * Per-user permission editor (P6, spec §1.8 — collapsible categories, a
 * "Customized" badge, "Reset to role defaults") over the API P4 shipped.
 *
 * This file is the SHELL. Every RULE lives in `permissionEditorModel.ts` (pure,
 * unit-tested without a DOM) and every SENTENCE in `permissionNotes.ts`, so the
 * two halves of INV-106 — what the screen offers and what it claims — are
 * testable without rendering anything. What the shell owns:
 *
 *  1. ONE pessimistic mutation. `PUT /users/:id/permissions` is a FULL
 *     REPLACEMENT (`user-permissions.service.ts:252-267` deletes every stored row
 *     the body omits), so the body is always `toOverrides(state)` — the whole
 *     desired set, never the slug that was clicked.
 *  2. The server's own sentences, shown and not paraphrased, with ONE mapped
 *     exception: the 409 names a maintenance script.
 *  3. Online-only, never queued (INV-19): `user` is not a /sync entity, so a
 *     permission edit has no offline representation to queue.
 *
 * Reasons are rendered as VISIBLE captions rather than Tooltips: every reason
 * here sits on a DISABLED switch, which cannot take focus, so a Tooltip would be
 * invisible to exactly the users who most need the sentence.
 */
export const SAVE_SUCCESS_MESSAGE = 'Permissions updated';
export const DEACTIVATED_TARGET_NOTE =
  'This account is deactivated and can no longer use StableTrack. Changes you make here are saved now.';
/**
 * The offline block, EXPORTED so the no-long-dash guard can walk it (#86). Online-only and it
 * says so: PUT/DELETE run under a clinic row lock and have no offline representation, so nothing
 * here is ever queued (INV-19).
 */
export const PERMISSIONS_OFFLINE_NOTICE = 'You need an internet connection to change permissions.';
/** The dialog's own heading. A function, because the person's name is half of it. */
export const permissionsDialogTitle = (name: string): string => `Permissions for ${name}`;
/**
 * The "Changed by" fallback (#86). `updatedBy` is a bare `User.id` with no join, so an actor who
 * has been deleted, or who belongs to another clinic, resolves to nothing in the loaded roster.
 * It used to print an em dash, which reads as a rendering fault rather than as an answer; this
 * says the true thing instead. A raw UUID was never an option.
 */
export const UNKNOWN_ACTOR_NAME = 'a team member';
export const DEACTIVATED_ADMIN_ROW_NOTE =
  "Giving this to a deactivated account does not count toward the clinic's requirement to keep one active person who can manage roles and permissions.";
/** The 409's own text names `scripts/backfill-user-claims.ts` — not a sentence for a clinic reader. */
export const STALE_OVERRIDE_UI_MESSAGE =
  "These permissions couldn't be saved because this account's data needs attention. Please contact support.";
export const TARGET_GONE_MESSAGE = 'This user no longer exists.';
export const SAVE_FALLBACK_MESSAGE = 'Permissions could not be saved. Please try again.';
export const LOAD_FAILED_MESSAGE =
  'These permissions could not be loaded. Close this window and try again.';
/**
 * Why Save is live on a form nobody has touched.
 *
 * A stored override whose `enabled` already EQUALS the role default is not
 * "customized" by the one predicate, so no chip and no caption marks it and no
 * switch sits anywhere unexpected — but `toOverrides` drops it, which makes
 * `isDirty` true the moment the form hydrates. The save is real work (the row goes
 * away), so the button stays enabled; what it must not do is report the tidy-up as
 * a change the admin made. This sentence is the difference.
 */
export const REDUNDANT_OVERRIDE_NOTE =
  'Saving will tidy up a stored setting that already matches the role default.';

export interface UserPermissionsDialogProps {
  open: boolean;
  onClose: () => void;
  /** The Team list ROW — the source of `name`, `role` and `active`, none of which the permissions view carries. */
  user: TeamUser;
  /** The loaded roster, for the "Changed by" join. A prop, never a second useQuery on the bare `'users'` key. */
  users: readonly TeamUser[];
  /** The loaded `['current-user-me']` payload. `id` is a `User.id`, NOT the Firebase uid (INV-25). */
  me: { id?: string } | undefined;
}

/**
 * Collapsed by default; a category holding a customized slug opens, and its chip points at it.
 *
 * Over the VISIBLE categories (#85): a customization on a hidden slug must not open a heading,
 * because the row it would point at is not in there.
 */
const seedExpanded = (
  view: UserPermissionsView,
  catalog: PermissionCatalog,
): Record<string, boolean> =>
  Object.fromEntries(
    visibleCategories(catalog).map((category) => [
      category.category,
      category.permissions.some((permission) => isCustomized(view, permission.slug)),
    ]),
  );

const UserPermissionsDialog: React.FC<UserPermissionsDialogProps> = ({
  open,
  onClose,
  user,
  users,
  me,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isOffline = useIsOffline();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { user: firebaseUser } = useAuth();
  const { claims } = useRBAC();
  const formatInstant = useFormatInstant();

  const [state, setState] = useState<EditorState | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [announcement, setAnnouncement] = useState<{
    slug: PermissionSlug;
    text: string;
    blocked: boolean;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /* Did the ADMIN move anything? A different question from `isDirty`, which asks
   * whether the body this form would POST differs from what the server holds — and
   * which is TRUE the instant the form hydrates for any target carrying a stored row
   * that already equals the role default (the state REDUNDANT_OVERRIDE_NOTE is
   * about). The discard confirm says "The switches you changed have not been saved",
   * so it may only be raised for switches somebody actually changed; Save keeps
   * `isDirty`, because the tidy-up is real work. */
  const [touched, setTouched] = useState(false);
  const [confirm, setConfirm] = useState<null | 'reset' | 'discard' | 'self-lockout'>(null);

  /* NOTHING may refetch this key while the form is up. The hydration effect below
   * is keyed on the view REFERENCE, and a concurrent edit by another admin answers
   * with a different payload — which defeats react-query's structural sharing, hands
   * back a new object and re-runs the effect, silently reverting every flipped
   * switch and every opened category. In an
   * offline-aware app a connectivity blip is an ordinary event, so `refetchOnReconnect`
   * (v3 default TRUE) is the live one; window focus is off globally
   * (index.tsx:331-340) and is turned off HERE as well, so this file no longer
   * depends on a setting another file owns.
   *
   * The MOUNT read is the exception, and it is `'always'`. A reopen is a fresh
   * MOUNT, not an `open` transition — `UserManagement.tsx:906-914` mounts this
   * dialog only while a row is selected and passes `open` as a bare literal — and
   * the app's client keeps an answer for five minutes (`index.tsx:335`) on the same
   * key the Team screen's role-change read warms. Hydrating that cache is not a
   * cosmetic staleness here: `toOverrides` builds a FULL REPLACEMENT body from the
   * loaded baseline, so a save on a five-minute-old view silently reinstates
   * whatever another admin revoked in between, with nothing on screen looking
   * wrong. Plain `true` would not do: with cached data present the form would
   * hydrate first and the background answer would re-run the hydration effect under
   * the admin — the very discard-without-a-confirm the triggers above are off for.
   * `isFetchedAfterMount` joins `ready` below so the body waits for THIS mount's
   * answer instead. A test pins both halves. */
  const { data: view, isError: viewFailed, isFetchedAfterMount } = useQuery(
    userPermissionsKey(user.id),
    () => getUserPermissions(user.id),
    {
      enabled: open,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
    },
  );
  const { data: catalog, isError: catalogFailed } = useQuery(
    PERMISSION_CATALOG_KEY,
    getPermissionCatalog,
    // Welded facts that only change on a deploy — and it is shared by the Team
    // screen's role-change confirm, which must not re-fetch it per dialog. The
    // hydration effect reads this identity too, so the same two triggers are off;
    // `refetchOnMount` is NOT, because the other screens mounting this key are
    // entitled to a fresh read once the hour is up.
    {
      enabled: open,
      staleTime: 60 * 60 * 1000,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );

  /* Hydrate the form from server data in an EFFECT — never react-query v3's
   * `onSuccess`, which re-fires on cache hits and has already wiped in-progress
   * Settings edits once (RemindersSettings.tsx:118-120). It re-runs when the view
   * identity changes, which is exactly what a successful save wants: the returned
   * view becomes the new baseline. TWO other things change that identity, and they
   * are handled differently:
   *   - a BACKGROUND refetch (reconnect, window focus) — turned off on both queries
   *     above, because this effect is a discard with no confirm and must never run
   *     under an admin who is mid-edit;
   *   - THIS MOUNT's refetch, which `refetchOnMount: 'always'` makes deliberate and
   *     unavoidable (it is what keeps a save off a five-minute-old baseline). It
   *     re-runs this effect, and that is SAFE rather than excepted: `ready` below
   *     joins `isFetchedAfterMount`, so the form is not on screen until this mount's
   *     answer has landed — there is no edit yet for the re-hydration to discard. */
  useEffect(() => {
    if (!open || !view || !catalog) return;
    setState(buildEditorState(view, catalog));
    setExpanded(seedExpanded(view, catalog));
    setAnnouncement(null);
    setErrorMessage(null);
    setTouched(false);
  }, [open, view, catalog]);

  useEffect(() => {
    if (open) return;
    setState(null);
    setAnnouncement(null);
    setErrorMessage(null);
    setTouched(false);
    setConfirm(null);
  }, [open]);

  const actorPermissions = useMemo(() => claims?.permissions ?? [], [claims]);
  /** ONE context for every rule the model answers, so no caller can pass a different actor. */
  const editorCtx = useMemo(() => ({ actorPermissions }), [actorPermissions]);

  /** slug -> label across EVERY category: a coupled change may name a slug in another one. */
  const labelOf = useMemo(() => {
    const map = new Map<string, string>();
    catalog?.categories.forEach((category) =>
      category.permissions.forEach((permission) => map.set(permission.slug, permission.label)),
    );
    return (slug: string) => map.get(slug) ?? slug;
  }, [catalog]);

  const handleToggle = useCallback(
    (slug: PermissionSlug, on: boolean) => {
      if (!state || !view || !catalog) return;
      const result = toggle(state, view, catalog, slug, on, editorCtx);
      if (!result.ok) {
        // The ceiling or the Owner lock caught a COUPLED change, so the whole
        // toggle is refused. Say so where the user is looking.
        setAnnouncement({ slug, text: result.reason, blocked: true });
        return;
      }
      setState(result.state);
      // A REFUSED toggle moves nothing, so it leaves nothing to discard: only this
      // arm counts as an edit.
      setTouched(true);
      setAnnouncement(
        result.alsoChanged.length > 0
          ? {
              slug,
              text: `${on ? 'Also turned on' : 'Also turned off'}: ${result.alsoChanged
                .map(labelOf)
                .join(', ')}`,
              blocked: false,
            }
          : null,
      );
    },
    [state, view, catalog, editorCtx, labelOf],
  );

  const roleSlug = view?.role ?? user.role;
  const loadFailed = viewFailed || catalogFailed;
  /* `isFetchedAfterMount` is what keeps the cached answer off the screen: with
   * `refetchOnMount: 'always'` react-query hands back the cached view immediately
   * and refetches behind it, so without this the form would hydrate from the stale
   * payload and be re-hydrated under the admin when the fresh one lands. The
   * spinner holds instead, and the first thing rendered is this mount's own read. */
  const ready = !!view && !!catalog && !!state && isFetchedAfterMount;
  const roleLabel = ROLE_LABELS[roleSlug] || roleSlug;
  /* The model's spelling, not a second one: this name and the Team screen's
   * role-change confirm feed the SAME rendered string (`iosVersionNote`), so
   * two spellings could show one clinic two different names for one person. */
  const firstName = firstNameOf(user.name);
  /* A `User.id` on both sides. NOT the Firebase uid: INV-25 says the two diverge
   * for every invited user, so a uid comparison would refresh the clinic's
   * original signup row and silently skip every admin who was invited. */
  const isSelfEdit = !!me?.id && view?.userId === me.id;

  const dirty = ready && isDirty(state!, view!, catalog!);
  const syncNarrowed = useMemo(
    () => (state && view && catalog ? syncNarrowedSlugs(state, view, catalog) : []),
    [state, view, catalog],
  );
  const selfLockoutSlugs = useMemo(
    () =>
      isSelfEdit && state && view
        ? narrowedSlugs(state, view).filter((slug) => SELF_LOCKOUT_SLUGS.includes(slug))
        : [],
    [isSelfEdit, state, view],
  );
  const resetReason = view && catalog ? resetBlockedReason(view, catalog, editorCtx) : null;
  /* Asked of the STORED ROWS rather than of the pristine form's dirtiness, so the
   * sentence is about what the server holds and never about anything the admin has
   * since done — and never about the two OTHER states that make a freshly hydrated
   * form dirty (a stored row for a slug the catalog no longer carries; a legacy
   * `manage:users: false` on an OWNER). `isDirty(pristine)` cannot tell the three
   * apart, and in the other two the caption's promise is false. The model names the
   * specific rows and its suite pins all four states. */
  const tidiesRedundantRow = useMemo(
    () => (view && catalog ? redundantOverrideSlugs(view, catalog).length > 0 : false),
    [view, catalog],
  );

  /* ONE pessimistic mutation for the whole form — Reset is a LOCAL action that
   * feeds this same Save, so there is one write path and one refusal path. */
  const save = useMutation(
    (overrides: PermissionOverrideInput[]) => putUserPermissions(user.id, overrides),
    {
      onSuccess: async (updated) => {
        queryClient.setQueryData(userPermissionsKey(user.id), updated);
        enqueueSnackbar(SAVE_SUCCESS_MESSAGE, { variant: 'success' });
        if (isSelfEdit) {
          // The adopt seam AuthProvider uses (AuthProvider.tsx:202/437/470).
          // `resetMePermissions` would blank every affordance instead.
          try {
            const response = await api.get('/users/me');
            if (firebaseUser?.uid) {
              acceptMePermissions(firebaseUser.uid, response.data?.permissions);
            }
          } catch {
            // The store keeps its last answer and the next mount re-reads. A
            // failed refresh must not read as a failed save — the write committed.
          }
          queryClient.invalidateQueries(['current-user-me']);
        }
        onClose();
      },
      onError: (error: any) => {
        const status = error?.response?.status;
        const message = error?.response?.data?.message;
        // The ONE mapped message: the 409's own text names a maintenance script.
        // Everything else is the server's sentence, authored for a clinic reader —
        // including a 403 SUBSCRIPTION_REQUIRED, which no interceptor touches.
        if (status === 409) {
          setErrorMessage(STALE_OVERRIDE_UI_MESSAGE);
          return;
        }
        if (status === 404) {
          setErrorMessage(TARGET_GONE_MESSAGE);
          // The roster's key is the bare string it has always been.
          queryClient.invalidateQueries('users');
          return;
        }
        // Nest's global ValidationPipe answers a malformed body with
        // `message: string[]` (main.ts), and React renders an array of strings with
        // no separator at all — two clauses run into one unreadable word. Every
        // refusal this screen is DESIGNED around is a string, so this is the
        // malformed-body path; it is still the path where the reader most needs to
        // be able to read it.
        const text = Array.isArray(message) ? message.join(' ') : message;
        setErrorMessage(text || SAVE_FALLBACK_MESSAGE);
      },
    },
  );

  const submit = useCallback(() => {
    if (!state || !view || !catalog) return;
    setErrorMessage(null);
    save.mutate(toOverrides(state, view, catalog));
  }, [state, view, catalog, save]);

  const handleSaveClick = () => {
    if (selfLockoutSlugs.length > 0) {
      setConfirm('self-lockout');
      return;
    }
    submit();
  };

  const handleCancel = () => {
    // `touched`, never `dirty` — see the state's own comment.
    if (touched) {
      setConfirm('discard');
      return;
    }
    onClose();
  };

  const handleResetConfirmed = () => {
    setConfirm(null);
    if (!state || !view || !catalog) return;
    setState(applyReset(state, view, catalog));
    setTouched(true);
    setAnnouncement(null);
  };

  /* The ROWS THE EDITOR SHOWS (#85). Hidden slugs keep their row in `state` — the save walks
   * that, not this — but nothing below renders one, and every count on screen is taken from
   * here, so "n of m on" and both Customized badges describe what the reader can actually see. */
  const shownCategories = useMemo(
    () => (catalog ? visibleCategories(catalog) : []),
    [catalog],
  );
  /* The BADGE counts visible rows; the two CONFIRMS below count ALL of them. Different
   * questions: a badge promises a row the reader can find, while a confirm states what the
   * write moves, and the write moves the hidden rows too. The model owns both spellings. */
  const shownCustomized = view && catalog ? visibleCustomizedCount(view, catalog) : 0;

  const customized = view ? customizedCount(view) : 0;
  const resetConfirmBody =
    customized > 0
      ? `Every ability for ${user.name} goes back to the ${roleLabel} defaults. That clears ${customized} customized permission${customized === 1 ? '' : 's'}, including any saved for an ability this screen does not show. Nothing is saved until you press Save changes.`
      : `Every ability for ${user.name} goes back to the ${roleLabel} defaults, and any switches you have changed here go back with them. Nothing is saved until you press Save changes.`;

  const saveDisabled = !dirty || isOffline || save.isLoading;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" data-testid="user-permissions-title">
          <Typography variant="h6" component="span">
            {permissionsDialogTitle(user.name)}
          </Typography>
          <Chip size="small" label={roleLabel} />
          {shownCustomized > 0 && (
            <Chip size="small" color="primary" variant="outlined" label="Customized" />
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <OfflineNotice sx={{ mb: 2 }}>{PERMISSIONS_OFFLINE_NOTICE}</OfflineNotice>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        )}
        {!user.active && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {DEACTIVATED_TARGET_NOTE}
          </Alert>
        )}
        {loadFailed && <Alert severity="error">{LOAD_FAILED_MESSAGE}</Alert>}
        {!loadFailed && !ready && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {!loadFailed &&
          ready &&
          shownCategories.map((category) => {
            const rows = category.permissions;
            const onCount = rows.filter((permission) => state!.rows[permission.slug]?.on).length;
            const categoryCustomized = rows.some(
              (permission) => state!.rows[permission.slug]?.customized,
            );
            return (
              <Accordion
                key={category.category}
                disableGutters
                expanded={!!expanded[category.category]}
                onChange={(_event, isExpanded) =>
                  setExpanded((prev) => ({ ...prev, [category.category]: isExpanded }))
                }
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  data-testid={`category-summary-${category.category}`}
                >
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography fontWeight={600}>{category.category}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {onCount} of {rows.length} on
                    </Typography>
                    {categoryCustomized && (
                      <Chip size="small" color="primary" variant="outlined" label="Customized" />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {rows.map((permission) => {
                    const row = state!.rows[permission.slug];
                    if (!row) return null;
                    const note = noteFor(permission);
                    /* The SERVER's grant ceiling as the optimistic hint design §C
                     * makes it — asked of the MODEL, which is where the rule lives
                     * and where `toggle` asks the same question of the whole coupled
                     * set. Re-deciding it here could make a switch dead while
                     * `toggle` would allow it. What this call site adds is the
                     * DIRECTION: `!row.on`, so OFF is never blocked and a slug the
                     * target holds still round-trips off→on.
                     * `buildEditorState` cannot do it: it sets `lockReason` for the
                     * Owner lock and the inert set only, and never sees the actor.
                     * `handleToggle`'s refusal arm stays for a COUPLED ceiling hit;
                     * this arm is the row whose OWN switch is dead, which is why the
                     * reason is a visible caption — a disabled switch takes no focus. */
                    const ceilingBlocked =
                      row.editable && !row.on && ceilingBlocks(view!, permission.slug, editorCtx);
                    const changedByName =
                      (row.changedBy
                        ? users.find((candidate) => candidate.id === row.changedBy)?.name
                        : undefined) ?? UNKNOWN_ACTOR_NAME;
                    return (
                      <Box
                        key={permission.slug}
                        data-testid={`permission-row-${permission.slug}`}
                        sx={{ py: 1 }}
                      >
                        <Box display="flex" alignItems="flex-start" gap={1}>
                          <Switch
                            size="small"
                            checked={row.on}
                            disabled={!row.editable || ceilingBlocked || isOffline}
                            onChange={(event) =>
                              handleToggle(permission.slug, event.target.checked)
                            }
                            inputProps={{ 'aria-label': `${permission.label} on/off` }}
                          />
                          <Box flexGrow={1} minWidth={0}>
                            {/* Only the LABEL of a non-editable row dims — the switch
                                already shows the state, and dimming half of thirty rows
                                would trade contrast for information nobody gains. */}
                            <Typography
                              variant="body2"
                              color={row.editable ? 'text.primary' : 'text.secondary'}
                            >
                              {permission.label}
                            </Typography>
                            {note && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {note}
                              </Typography>
                            )}
                            {/* The Owner lock is the only reason a VISIBLE row carries since
                                #85: the inert rows, whose lockReason used to be the copy
                                deck's own sentence (hence the `!== note` guard that stood
                                here, against printing it twice), are not rendered at all. */}
                            {row.lockReason && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {row.lockReason}
                              </Typography>
                            )}
                            {ceilingBlocked && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {CEILING_BLOCK_REASON}
                              </Typography>
                            )}
                            {row.customized && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {/* A missing date DROPS its whole part rather than printing a
                                    placeholder after a separator (#86): "Changed by X ·" with
                                    nothing behind it reads as a truncated render. */}
                                {`Customized · Changed by ${changedByName}`}
                                {row.changedAt ? ` · ${formatInstant(row.changedAt)}` : ''}
                              </Typography>
                            )}
                            {!user.active && permission.slug === 'manage:roles_permissions' && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {DEACTIVATED_ADMIN_ROW_NOTE}
                              </Typography>
                            )}
                            <Box
                              role="status"
                              aria-live="polite"
                              data-testid={`permission-announcement-${permission.slug}`}
                            >
                              {announcement?.slug === permission.slug && (
                                <Typography
                                  variant="caption"
                                  color={announcement.blocked ? 'error' : 'text.secondary'}
                                  display="block"
                                >
                                  {announcement.text}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })}
      </DialogContent>
      <Box data-testid="user-permissions-footer">
        <Box sx={{ px: 3, pt: 2 }}>
          {/* The note sits HERE, beside Save — not at the top of a thirty-row body
              that can be scrolled past before the button is pressed. It informs and
              holds nothing: #79 reversed the release order, so the instruction is
              "update the app", which the clinic can act on. */}
          <IosVersionNote firstName={firstName} syncNarrowed={syncNarrowed} />
          {resetReason && (
            <Typography variant="caption" color="text.secondary" display="block">
              {resetReason}
            </Typography>
          )}
          {ready && tidiesRedundantRow && (
            <Typography variant="caption" color="text.secondary" display="block">
              {REDUNDANT_OVERRIDE_NOTE}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block">
            Changes apply within a few seconds. {firstName} may need to reload the page before
            buttons appear or disappear.
          </Typography>
        </Box>
        <DialogActions
          sx={{
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1,
            px: 3,
            pb: 2,
          }}
        >
          <Button
            onClick={() => setConfirm('reset')}
            disabled={!ready || isOffline || save.isLoading || !!resetReason}
          >
            Reset to role defaults
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={handleCancel}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveClick} disabled={saveDisabled}>
            Save changes
          </Button>
        </DialogActions>
      </Box>

      {/* Each confirm is MOUNTED only while it is the open one, rather than left in
          the tree with `open={false}`. A dismissed MUI Dialog stays in the DOM for
          its exit transition, and while it does, the editor behind it is still
          `aria-hidden` — so its own "Cancel" would go on swallowing clicks aimed at
          the footer for a fifth of a second after it was answered. A control that
          has already done its job must not still be the one that takes the press. */}
      {confirm === 'reset' && (
      <Dialog open onClose={() => setConfirm(null)}>
        <DialogTitle>Reset to {roleLabel} defaults?</DialogTitle>
        <DialogContent>
          <DialogContentText>{resetConfirmBody}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleResetConfirmed}>
            Reset switches
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {confirm === 'discard' && (
      <Dialog open onClose={() => setConfirm(null)}>
        <DialogTitle>Discard your changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The switches you changed have not been saved. Closing now leaves {user.name}'s
            permissions exactly as they were.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Keep editing</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirm(null);
              onClose();
            }}
          >
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {confirm === 'self-lockout' && (
      <Dialog open onClose={() => setConfirm(null)}>
        <DialogTitle>Remove your own access?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are turning off{' '}
            {selfLockoutSlugs.map((slug) => labelOf(slug)).join(' and ')} for your own account.
            Once you save, you will not be able to open this screen again yourself. Another team
            member who can manage roles and permissions will have to turn it back on.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirm(null);
              submit();
            }}
          >
            Save anyway
          </Button>
        </DialogActions>
      </Dialog>
      )}
    </Dialog>
  );
};

export default UserPermissionsDialog;
